import { useEffect, useState } from "react";
import { CalendarClock, Loader2, Plus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const PRIORITY_STYLE = {
  alta: "bg-red-50 text-red-700",
  media: "bg-amber-50 text-amber-700",
  bassa: "bg-slate-100 text-slate-600",
};

const Agenda = () => {
  const { t, ts } = useI18n();
  const today = new Date().toISOString().slice(0, 10);
  const [day, setDay] = useState(today);
  const [tasks, setTasks] = useState([]);
  const [plan, setPlan] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [form, setForm] = useState({ title: "", priority: "media", duration_min: 30 });

  const load = () => api.get("/tasks", { params: { day } }).then(({ data }) => setTasks(data));

  useEffect(() => {
    load();
  }, [day]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    await api.post("/tasks", { ...form, due_date: day });
    setForm({ title: "", priority: "media", duration_min: 30 });
    load();
  };

  const toggle = async (task) => {
    await api.put(`/tasks/${task.id}`, { ...task, done: !task.done });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/tasks/${id}`);
    load();
  };

  const planDay = async () => {
    setPlanning(true);
    try {
      const { data } = await api.post("/ai/plan-day");
      setPlan(data);
      toast.success(t("agenda.planned"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || t("agenda.planFailed"));
    } finally {
      setPlanning(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3" data-testid="agenda-page">
      <div className="space-y-5 lg:col-span-2">
        <div className="card-flat p-6">
          <form onSubmit={add} className="grid gap-3 sm:grid-cols-[1fr_120px_100px_auto]">
            <div>
              <Label>{t("agenda.task")}</Label>
              <Input
                data-testid="task-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={t("agenda.taskPlaceholder")}
                className="mt-1.5 focus:ring-2 focus:ring-blue-600"
              />
            </div>
            <div>
              <Label>{t("agenda.priority")}</Label>
              <select
                data-testid="task-priority"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:ring-2 focus:ring-blue-600"
              >
                <option value="alta">{t("agenda.high")}</option>
                <option value="media">{t("agenda.medium")}</option>
                <option value="bassa">{t("agenda.low")}</option>
              </select>
            </div>
            <div>
              <Label>{t("agenda.minutes")}</Label>
              <Input
                data-testid="task-duration"
                type="number"
                value={form.duration_min}
                onChange={(e) => setForm({ ...form, duration_min: Number(e.target.value) })}
                className="font-mono-num mt-1.5 focus:ring-2 focus:ring-blue-600"
              />
            </div>
            <Button
              data-testid="add-task"
              type="submit"
              className="mt-auto gap-2 bg-blue-600 text-white hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" /> {t("common.add")}
            </Button>
          </form>
        </div>

        <div className="card-flat p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-semibold md:text-lg">{t("agenda.task")}</h2>
            <Input
              data-testid="agenda-day"
              type="date"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              className="w-auto focus:ring-2 focus:ring-blue-600"
            />
          </div>
          <ul className="mt-4 space-y-2" data-testid="tasks-list">
            {tasks.length === 0 && (
              <li className="py-8 text-center text-sm text-slate-500">{t("agenda.empty")}</li>
            )}
            {tasks.map((task, i) => (
              <li
                key={task.id}
                className="rise flex items-center gap-3 rounded-md border border-slate-200 px-3 py-2.5 transition-colors duration-200 hover:bg-slate-50"
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <Checkbox
                  data-testid={`toggle-task-${task.id}`}
                  checked={task.done}
                  onCheckedChange={() => toggle(task)}
                />
                <span className={`flex-1 text-sm ${task.done ? "text-slate-400 line-through" : "font-medium"}`}>
                  {task.title}
                </span>
                <span className={`rounded px-2 py-0.5 text-xs font-semibold ${PRIORITY_STYLE[task.priority]}`}>
                  {ts(task.priority)}
                </span>
                <span className="font-mono-num text-xs text-slate-500">{task.duration_min}m</span>
                <button
                  data-testid={`delete-task-${task.id}`}
                  onClick={() => remove(task.id)}
                  className="text-slate-400 transition-colors duration-200 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="card-flat relative overflow-hidden border-indigo-500/30 bg-slate-950 p-6 text-slate-100 grain">
        <div className="relative">
          <p className="flex items-center gap-2 text-sm font-extrabold tracking-tight">
            <Sparkles className="h-4 w-4 text-indigo-400" /> {t("agenda.aiTitle")}
          </p>
          <p className="mt-1 text-xs text-slate-400">{t("agenda.aiHint")}</p>
          <Button
            data-testid="plan-day-button"
            onClick={planDay}
            disabled={planning}
            className="mt-4 w-full gap-2 bg-indigo-600 text-white hover:bg-indigo-500"
          >
            {planning ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
            {t("agenda.planNow")}
          </Button>

          {plan && (
            <div className="mt-6 space-y-3" data-testid="ai-plan">
              <p className="text-sm text-slate-300">{plan.summary}</p>
              {(plan.blocks || []).map((b, i) => (
                <div
                  key={i}
                  className="rise rounded-md border border-slate-800 bg-slate-900 p-3"
                  style={{ animationDelay: `${i * 70}ms` }}
                >
                  <p className="font-mono-num text-xs text-indigo-300">
                    {b.start} – {b.end}
                  </p>
                  <p className="mt-1 text-sm font-semibold">{b.title}</p>
                  <p className="text-xs text-slate-400">{b.why}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Agenda;
